import Reveal from "@/components/Reveal";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { SITE } from "@/data/site";

export default function Location() {
  return (
    <section id="visit" data-testid="location-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-clay">
            <span className="h-px w-10 bg-clay" /> Visit — two clinics
          </p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tighter text-forest sm:text-5xl" data-testid="location-heading">
            West End on Alberni, East Van on Hastings.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
          {SITE.locations.map((loc, i) => (
            <Reveal key={loc.id} delay={i * 0.12}>
              <div className="flex h-full flex-col border border-forest/10" data-testid={`location-card-${loc.id}`}>
                <div className="overflow-hidden" data-testid={`location-map-${loc.id}`}>
                  <iframe
                    title={`Map — ${loc.clinic}, Vancouver`}
                    src={loc.mapEmbed}
                    className="h-64 w-full grayscale-[30%]"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="flex flex-1 flex-col p-8 lg:p-10">
                  <p className="text-xs uppercase tracking-[0.25em] text-clay">{loc.area}</p>
                  <h3 className="mt-3 font-serif text-3xl font-light tracking-tight text-forest">{loc.clinic}</h3>
                  <address className="mt-4 flex items-start gap-3 text-sm not-italic leading-relaxed text-forest/70">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-clay" /> {loc.address}
                  </address>
                  <a href={loc.phoneHref} data-testid={`location-phone-${loc.id}`} className="mt-3 flex items-center gap-3 text-sm text-forest/70 transition-colors duration-300 hover:text-pine">
                    <Phone className="h-4 w-4 shrink-0 text-clay" /> {loc.phone}
                  </a>
                  <p className="mt-4 text-sm leading-relaxed text-forest/60">{loc.note}</p>
                  <a
                    href={loc.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`location-book-${loc.id}`}
                    className="group mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-pine px-7 py-3.5 text-xs uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-pine/25"
                  >
                    Book at {loc.clinic.split(" ")[0]} {loc.clinic.split(" ")[1]}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="relative overflow-hidden rounded-[2rem]">
              <img
                src="/images/west-end.jpg"
                alt="Inside Jordan's treatment room at West End Physiotherapy"
                data-testid="location-image-west-end"
                className="h-72 w-full object-cover lg:h-[420px]"
              />
              <p className="absolute bottom-5 left-6 rounded-full bg-bone/90 px-5 py-2 text-xs uppercase tracking-[0.2em] text-pine backdrop-blur" data-testid="location-image-west-end-caption">
                Inside West End Physiotherapy
              </p>
            </div>
            <div className="relative overflow-hidden rounded-[2rem]">
              <img
                src="/images/paper-crane.jpg"
                alt="Inside the treatment room at Paper Crane Massage Therapy, East Vancouver"
                data-testid="location-image-paper-crane"
                className="h-72 w-full object-cover lg:h-[420px]"
              />
              <p className="absolute bottom-5 left-6 rounded-full bg-bone/90 px-5 py-2 text-xs uppercase tracking-[0.2em] text-pine backdrop-blur" data-testid="location-image-paper-crane-caption">
                Inside Paper Crane Massage Therapy
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
